import { useEffect, useState } from "react";
import { ShareExpiredPage, isExpiredError } from "@/components/ShareExpiry";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { daDate } from "@/lib/date";
import { LayoutGrid, CheckCircle2, MinusCircle, ClipboardList, ListChecks, FileCheck, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAT_BADGE = { Afvigelse: "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]", Observation: "bg-[#F4EFEC] text-[#806B61] border-[#E6DDD6]", Forslag: "bg-[#E7EADF] text-[#5f6b4a] border-[#D3D8C6]" };
const ST_BADGE = { Åben: "bg-[#F1DEDA] text-[#A83232]", "Afventer kontrol": "bg-[#F4EFEC] text-[#806B61]", Lukket: "bg-[#E7EADF] text-[#5f6b4a]" };

const Section = ({ title, id, children }) => (
  <section id={id} className="mt-14 md:mt-20 scroll-mt-24" data-testid={`pub-a-section-${id}`}>
    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-6" style={{ color: "#1A1919" }}>{title}</h2>
    {children}
  </section>
);
const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border p-6 md:p-8 ${className}`} style={{ borderColor: "#EBE8E3", boxShadow: "0 4px 24px rgba(0,0,0,0.03)" }}>{children}</div>
);
const Meta = ({ label, value }) => value ? (
  <div><p className="text-xs font-semibold uppercase tracking-[0.1em]" style={{ color: "#A8A29E" }}>{label}</p><p className="mt-1.5 text-base font-medium" style={{ color: "#1A1919" }}>{value}</p></div>
) : null;

const initials = (n) => (n || "").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
function AuditorAvatar({ url, name }) {
  const cls = "h-16 w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 rounded-full shrink-0";
  return url ? (
    <img src={url} alt={name || "Auditor"} data-testid="pub-a-auditor-avatar" className={`${cls} object-cover`} style={{ border: "1px solid #EBE8E3" }} />
  ) : (
    <div data-testid="pub-a-auditor-avatar-fallback" className={`${cls} flex items-center justify-center font-semibold`} style={{ background: "#F1DEDA", color: "#A83232", border: "1px solid #E9C9C3", fontSize: 22 }}>{initials(name) || "?"}</div>
  );
}

import { PrintPageStyle } from "@/components/print/PrintFrontMatter";
import { AuditPrintDoc } from "@/components/print/AuditPrint";
import { PrintButton } from "@/components/print/PrintButton";

export default function PublicAuditReport() {
  const { token } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  const [active, setActive] = useState("top");

  useEffect(() => {
    const m1 = document.createElement("meta"); m1.name = "referrer"; m1.content = "no-referrer"; const m2 = document.createElement("meta"); m2.name = "robots"; m2.content = "noindex, nofollow"; document.head.append(m1, m2);
    return () => { m1.remove(); m2.remove(); };
  }, []);
  useEffect(() => {
    document.title = "BSM A/S — Auditrapport";
    if (!document.getElementById("gf-manrope")) {
      const l = document.createElement("link"); l.id = "gf-manrope"; l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap";
      document.head.appendChild(l);
    }
    api.get(`/public/audit/${token}`).then((r) => setA(r.data)).catch((e) => setErr(isExpiredError(e) ? "expired" : true));
  }, [token]);

  const font = { fontFamily: "Manrope, Inter, ui-sans-serif, system-ui, sans-serif" };
  const nav = [
    { id: "top", label: "Overblik", icon: LayoutGrid },
    { id: "omraader", label: "Gennemgåede områder", icon: ClipboardList },
    { id: "fund", label: "Fund", icon: ListChecks },
    { id: "konklusion", label: "Konklusion", icon: FileCheck },
  ];

  useEffect(() => {
    if (!a) return;
    const onScroll = () => { let cur = "top"; for (const n of nav) { const el = document.getElementById(n.id); if (el && el.getBoundingClientRect().top <= 140) cur = n.id; } setActive(cur); };
    onScroll(); window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [a]); // eslint-disable-line
  const go = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };

  if (err === "expired") return <ShareExpiredPage />;
  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ background: "#F3F1EC", ...font }} data-testid="pub-a-not-found">
      <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#A83232" }}>BSM A/S</p>
      <h1 className="text-3xl font-light mt-4" style={{ color: "#1A1919" }}>Dette rapportlink er ikke længere aktivt.</h1>
      <p className="mt-3 max-w-md" style={{ color: "#A8A29E" }}>Kontakt BSM A/S for at få et opdateret link.</p>
    </div>
  );
  if (!a) return <div className="min-h-screen flex items-center justify-center" style={{ background: "#FBFAF8", color: "#A8A29E", ...font }}>Indlæser rapport…</div>;

  const img = (fid, id) => `${BACKEND}/api/public/audit/${token}/img/${fid}/${id}`;
  return (
    <div className="min-h-screen flex overflow-x-hidden pub-print-doc" style={{ background: "#FBFAF8", color: "#1A1919", ...font }} data-testid="pub-audit">
      <PrintPageStyle title={a.title} date={daDate(a.actual_date)} />
      <AuditPrintDoc a={a} token={token} />
      <aside className="hidden lg:flex flex-col w-64 shrink-0 fixed inset-y-0 bg-white border-r z-30" style={{ borderColor: "#EBE8E3" }}>
        <div className="flex items-center px-6 h-20 shrink-0"><img src="/bsm-logo.png" alt="BSM A/S" className="h-9 w-auto" /></div>
        <div className="px-6 pb-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "#A8A29E" }}>Intern audit</p>
          <p className="text-sm font-semibold mt-1" style={{ color: "#1A1919" }}>{a.audit_no} · {daDate(a.actual_date)}</p>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
          {nav.map((n) => {
            const A = active === n.id;
            return (
              <button key={n.id} onClick={() => go(n.id)} data-testid={`pub-a-nav-${n.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl text-left transition-colors ${A ? "bg-[#F1DEDA] text-[#A83232]" : "text-stone-600 hover:bg-[#F3F1EC] hover:text-stone-900"}`}>
                <n.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} /> <span className="truncate">{n.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="border-t p-5 text-xs" style={{ borderColor: "#EBE8E3", color: "#A8A29E" }}>BSM A/S · {new Date().getFullYear()}</div>
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0 overflow-x-hidden">
        <div className="lg:hidden sticky top-0 z-20 bg-white/90 backdrop-blur border-b" style={{ borderColor: "#EBE8E3" }}>
          <div className="flex items-center justify-between px-5 h-16"><img src="/bsm-logo.png" alt="BSM A/S" className="h-7 w-auto" /><p className="text-[11px]" style={{ color: "#A8A29E" }}>{a.audit_no}</p></div>
          <div className="flex gap-2 overflow-x-auto px-5 pb-3">
            {nav.map((n) => <button key={n.id} onClick={() => go(n.id)} className={`whitespace-nowrap text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${active === n.id ? "bg-[#F1DEDA] text-[#A83232] border-[#E9C9C3]" : "text-stone-600 border-[#EBE8E3]"}`}>{n.label}</button>)}
          </div>
        </div>

        <main className="max-w-5xl mx-auto px-6 md:px-12 pb-24">
          <div className="pt-6 flex justify-end"><PrintButton filename={`Audit ${a.audit_no}`} /></div>
          <section id="top" className="pt-12 md:pt-20 scroll-mt-24 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-x-10 gap-y-8 items-start">
            <div className="min-w-0 order-1">
              <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#A83232" }}>Intern audit · Rapport</p>
              <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mt-3 break-words [overflow-wrap:anywhere]" style={{ color: "#1A1919" }} data-testid="pub-a-title">{a.title}</h1>
              <p className="text-sm mt-3" style={{ color: "#78716C" }}>Afsluttet af {a.completed_by_name || "—"} · {a.completed_at ? daDate(a.completed_at) : "—"}</p>
            </div>

            {a.auditor_name ? (
              <div className="w-full bg-white rounded-2xl border p-5 md:p-6 order-2" style={{ borderColor: "#EBE8E3", boxShadow: "0 4px 24px rgba(0,0,0,0.03)" }} data-testid="pub-a-auditor-card">
                <p className="text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: "#A83232" }}>Auditor</p>
                <div className="mt-4 flex items-center gap-4">
                  <AuditorAvatar url={a.auditor_avatar ? `${BACKEND}${a.auditor_avatar}` : null} name={a.auditor_name} />
                  <div className="min-w-0">
                    <p className="text-base font-semibold break-words [overflow-wrap:anywhere]" data-testid="pub-a-auditor-name" style={{ color: "#1A1919" }}>{a.auditor_name}</p>
                    {a.auditor_title && <p className="text-sm mt-0.5" style={{ color: "#78716C" }}>{a.auditor_title}</p>}
                  </div>
                </div>
              </div>
            ) : <div className="hidden lg:block order-2" />}

            <div className="order-3 lg:col-span-2">
            <Card>
              <div className="grid sm:grid-cols-2 gap-6">
                <Meta label="Planlagt dato" value={daDate(a.planned_date)} />
                <Meta label="Faktisk auditdato" value={daDate(a.actual_date)} />
                <Meta label="Deltagere" value={a.participants} />
                <Meta label="Planlagte punkter/krav" value={a.planned_points} />
                <Meta label="Auditerede punkter / agenda" value={a.audited_points} />
              </div>
              {(a.audited_requirements || []).length > 0 && <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.1em]" style={{ color: "#A8A29E" }}>Auditerede lovkrav og andre krav</p>
                <div className="flex flex-wrap gap-2 mt-2">{a.audited_requirements.map((r, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-lg" style={{ background: "#F3F1EC", color: "#57534E" }}>{r}</span>)}</div>
              </div>}
              <div className="flex flex-wrap gap-2 mt-6">
                <span className="px-2.5 py-1 rounded-full text-sm bg-[#F1DEDA] text-[#A83232]">{a.counts?.Afvigelse || 0} afvigelser</span>
                <span className="px-2.5 py-1 rounded-full text-sm bg-[#F4EFEC] text-[#806B61]">{a.counts?.Observation || 0} observationer</span>
                <span className="px-2.5 py-1 rounded-full text-sm bg-[#E7EADF] text-[#5f6b4a]">{a.counts?.Forslag || 0} forslag</span>
              </div>
            </Card>
            </div>
          </section>

          <Section id="omraader" title="Gennemgåede områder">
            <Card>
              <div className="space-y-3">
                {(a.area_reviews || []).map((r) => (
                  <div key={r.area_id} className="flex items-start gap-2.5 text-[15px]">
                    {r.status === "reviewed" ? <CheckCircle2 className="h-5 w-5 text-[#5f6b4a] mt-0.5 shrink-0" /> : <MinusCircle className="h-5 w-5 text-[#A83232] mt-0.5 shrink-0" />}
                    <div><span className="font-medium" style={{ color: "#1A1919" }}>{r.area_name}</span>
                      {r.status === "reviewed" && r.note && <p className="whitespace-pre-wrap" style={{ color: "#57534E" }}>{r.note}</p>}
                      {r.status === "not_reviewed" && <p style={{ color: "#A83232" }}>Ikke gennemgået — {r.reason || "ingen begrundelse"}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </Section>

          <Section id="fund" title="Fund">
            {(a.findings || []).length === 0 ? <Card><p className="text-sm" style={{ color: "#A8A29E" }}>Ingen fund.</p></Card> : (
              <div className="space-y-3">
                {a.findings.map((f) => (
                  <Card key={f.id} className="!p-5" data-testid={`pub-a-finding-${f.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono" style={{ color: "#A8A29E" }}>{f.finding_no}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${CAT_BADGE[f.category]}`}>{f.category}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${ST_BADGE[f.status]}`}>{f.status}</span>
                      <span className="text-xs" style={{ color: "#78716C" }}>{f.area_name}</span>
                    </div>
                    <p className="text-[15px] mt-2 whitespace-pre-wrap" style={{ color: "#44403C" }}>{f.description}</p>
                    {(f.images || []).length > 0 && <div className="flex flex-wrap gap-2 mt-3">{f.images.map((im) => (
                      <a key={im.id} href={img(f.id, im.id)} target="_blank" rel="noreferrer"><img src={img(f.id, im.id)} alt="" className="h-24 w-24 object-cover rounded-lg border" style={{ borderColor: "#EBE8E3" }} /></a>
                    ))}</div>}
                    {(f.followup_images || []).length > 0 && f.status !== "Lukket" && <div className="flex flex-wrap gap-2 mt-3">{f.followup_images.map((im) => (
                      <a key={im.id} href={img(f.id, im.id)} target="_blank" rel="noreferrer"><img src={img(f.id, im.id)} alt="" className="h-24 w-24 object-cover rounded-lg border" style={{ borderColor: "#EBE8E3" }} /></a>
                    ))}</div>}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-2 text-xs" style={{ color: "#78716C" }}>
                      <span>Ansvarlig: {f.responsible_name || "—"}</span><span>Frist: {daDate(f.due_date)}</span>
                    </div>
                    {(f.files || []).length > 0 && <div className="mt-3 space-y-1.5" data-testid={`pub-a-finding-files-${f.id}`}>
                      <p className="text-xs font-semibold" style={{ color: "#78716C" }}>Yderligere dokumentation</p>
                      {f.files.map((fl) => (
                        <a key={fl.id} href={`${BACKEND}/api/public/audit/${token}/file/${f.id}/${fl.id}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg border px-3 py-2 hover:bg-[#FAF9F7]" style={{ borderColor: "#EBE8E3" }} data-testid={`pub-a-finding-file-${fl.id}`}>
                          <FileText className="h-4 w-4 shrink-0" style={{ color: "#A8A29E" }} />
                          <span className="text-sm truncate flex-1" style={{ color: "#1A1919" }}>{fl.original_filename}</span>
                          <span className="text-xs shrink-0" style={{ color: "#A8A29E" }}>{Math.max(1, Math.round((fl.size || 0) / 1024))} KB</span>
                        </a>
                      ))}
                    </div>}
                    {f.status === "Lukket" && (
                      <div className="mt-3 rounded-xl p-3 text-sm" style={{ background: "#F6F8F1", border: "1px solid #D3D8C6" }}>
                        <span className="font-medium text-[#5f6b4a]">Løsning:</span> <span className="whitespace-pre-wrap" style={{ color: "#44403C" }}>{f.resolution || "—"}</span>
                        <p className="text-xs mt-1" style={{ color: "#78716C" }}>Lukket af {f.closed_by_name || "—"} · {f.closed_at ? daDate(f.closed_at) : "—"}</p>
                        {(f.followup_images || []).length > 0 && <div className="flex flex-wrap gap-2 mt-2">{f.followup_images.map((im) => (
                          <a key={im.id} href={img(f.id, im.id)} target="_blank" rel="noreferrer"><img src={img(f.id, im.id)} alt="" className="h-20 w-20 object-cover rounded-lg border" style={{ borderColor: "#D3D8C6" }} /></a>
                        ))}</div>}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </Section>

          <Section id="konklusion" title="Samlet konklusion">
            <Card><p className="text-[15px] leading-relaxed whitespace-pre-wrap" style={{ color: "#44403C" }}>{a.conclusion || "—"}</p></Card>
          </Section>

          <p className="text-center text-xs mt-16" style={{ color: "#A8A29E" }}>BSM A/S · Intern auditrapport</p>
        </main>
      </div>
    </div>
  );
}
